import Navbar from "../Navbar"
import Footer from "../Footer"
import { useNavigate } from "react-router-dom"
import { motion, useScroll, useTransform } from "framer-motion";
import tankCleaning from "../../../static/images/services/tc.jpg";

const IMOTankContainerCertification = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0 },
  };
  const navigate = useNavigate()

  const features = [
    {
      title: "Pre-Loading Inspection",
      description:
        "Comprehensive inspection of IMO tank containers before loading to ensure compliance with international standards.",
    },
    {
      title: "Cleanliness Verification",
      description:
        "Thorough cleanliness testing and verification following IMO guidelines and international maritime regulations.",
    },
    {
      title: "Safety Compliance Checks",
      description:
        "Complete safety assessments including structural integrity, seal verification, and documentation review.",
    },
    {
      title: "Certificate Issuance",
      description:
        "Official cleanliness certificates issued in accordance with international maritime standards and regulations.",
    },
    {
      title: "Quick Turnaround",
      description: "Fast and efficient certification process to minimize delays in your shipping operations.",
    },
    {
      title: "Documentation",
      description: "Complete documentation package with photographs, measurements, and professional reports.",
    },
  ]

  const benefits = [
    "Ensures international compliance",
    "Prevents cargo contamination",
    "Reduces liability risks",
    "Speeds up port operations",
    "Professional documentation",
    "Cost-effective solution",
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] mt-20 overflow-hidden">
        <img
          src={tankCleaning}
          alt="IMO Tank Container Certification"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-black-800/90 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">IMO Tank Container Certification</h1>
              <p className="text-xl text-blue-100">
                Comprehensive checking and issuing of cleanliness certificates for all IMO tank containers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-semibold text-orange mb-6">Service Overview</h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              United Marine Agency LLP provides comprehensive IMO tank container certification services. We ensure that
              your containers meet all international maritime standards before shipment. Our certified surveyors conduct
              thorough inspections and issue official cleanliness certificates that are recognized globally.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              With our quick turnaround time and professional approach, we help streamline your operations while
              maintaining the highest quality standards. Our expertise spans all types of IMO tank containers used for
              transporting liquid chemicals, food products, and other commodities.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Key Features */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-6 text-center">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Why Choose Our Service</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-orange mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-lg text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Our Process</h2>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mx-auto">
              {[
                {
                  num: "1",
                  title: "Scheduling",
                  desc: "Contact us to schedule the inspection at your preferred location and time",
                },
                {
                  num: "2",
                  title: "Container Inspection",
                  desc: "Our certified surveyors conduct thorough inspection and testing of the container",
                },
                {
                  num: "3",
                  title: "Cleanliness Testing",
                  desc: "Detailed cleanliness verification following international standards",
                },
                {
                  num: "4",
                  title: "Documentation",
                  desc: "Complete reports with photographs and official certification",
                },
                {
                  num: "5",
                  title: "Certificate Issuance",
                  desc: "Official IMO cleanliness certificate issued promptly",
                },
              ].map((step, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-orange text-white font-semibold">
                      {step.num}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>
      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  )
}

export default IMOTankContainerCertification;